// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import { Box, Collapse, Flex, Stack } from '@mantine/core';
import { useLocation } from 'react-router';

import { isNavActionOption, moduleRoute, type NavOption } from 'app/Layout/NavBar/globalNavigationMenu';
import { NavItem } from 'app/Layout/NavBar/NavBarContent/NavItem';
import { NavPopover } from 'app/Layout/NavBar/NavBarContent/NavPopover';
import { NavSectionHeader } from 'app/Layout/NavBar/NavBarContent/NavSectionHeader';
import styledNavLinkClasses from 'app/Layout/NavBar/NavBarContent/StyledNavLink.module.css';
import { navbarAnimationDuration, navbarAnimationTimingFunction } from 'app/Layout/NavBar/useAppShell';
import type { EvolveIconName } from 'assets/icons/EvolveIcon';
import { uuIDValidator } from 'helpers/uuIDValidator';

export const NavBarSection = ({
  navData,
  navbarExpanded,
  popoverOpened,
  onPopoverChanged,
  collapsible = true,
  onOptionClick,
}: {
  navData: {
    label: string;
    icon: EvolveIconName;
    options: NavOption[];
  };
  navbarExpanded: boolean;
  popoverOpened: boolean;
  onPopoverChanged: (popoverOpened: boolean) => void;
  /** @defaultValue true */
  collapsible?: boolean;
  onOptionClick?: (option: NavOption) => void;
}) => {
  const location = useLocation();

  const [navOptionsOpened, setNavOptionsOpened] = useState(true);

  const routeIsActive = useCallback(
    (option: NavOption) => {
      if (isNavActionOption(option)) return false;
      const { route, matchSubRoutes } = option;
      const pathNames = location.pathname.split('/').filter((x) => x);
      const pathNameWithoutUUID = pathNames.filter((x) => !uuIDValidator(x)).join('/');
      const optionPath = `${moduleRoute}${route}`;
      return (
        pathNameWithoutUUID === optionPath ||
        (matchSubRoutes === true && pathNameWithoutUUID.startsWith(`${optionPath}/`))
      );
    },
    [location.pathname],
  );

  return (
    <Box key={navData.label} mb="xs">
      <Flex align="center">
        <NavPopover
          opened={popoverOpened}
          onChange={onPopoverChanged}
          position="right-start"
          disabled={navbarExpanded}
          label={navData.label}
          target={
            collapsible ? (
              <NavSectionHeader
                navData={navData}
                navbarExpanded={navbarExpanded}
                collapsible
                navOptionsOpened={navOptionsOpened}
                onClick={() => {
                  if (navbarExpanded) {
                    setNavOptionsOpened((s) => !s);
                  }
                }}
              />
            ) : (
              <NavSectionHeader navData={navData} navbarExpanded={navbarExpanded} collapsible={false} />
            )
          }
        >
          {navData.options.map((opt) => (
            <NavItem
              key={`${navData.label}-${opt.label}`}
              item={opt}
              onClick={() => {
                onPopoverChanged(false);
                onOptionClick?.(opt);
              }}
              active={routeIsActive(opt)}
              withLabel
            />
          ))}
        </NavPopover>
      </Flex>
      <Collapse
        expanded={navbarExpanded && navOptionsOpened}
        transitionDuration={navbarAnimationDuration}
        transitionTimingFunction={navbarAnimationTimingFunction}
      >
        <Stack gap={0} flex={1} pt={6} pl={18}>
          {navData.options.map((opt) => {
            const isActive = routeIsActive(opt);
            return (
              <Box
                key={`${navData.label}-${opt.label}`}
                className={
                  isActive
                    ? [styledNavLinkClasses.navStackItem, styledNavLinkClasses.activeItem].join(' ')
                    : styledNavLinkClasses.navStackItem
                }
              >
                <NavItem item={opt} withLabel={navbarExpanded} active={isActive} onClick={() => onOptionClick?.(opt)} />
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};
